import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { api } from '../../../core/apiClient';
import { useAuth } from '../../../auth/AuthContext';

interface SupplierEvaluation {
  id: string;
  productService?: string;
  criticality?: string;
  criterion?: string;
  quality?: number;
  serviceLevel?: number;
  price?: number;
  environmental?: number;
  total?: number;
  qualification?: string;
  evaluatedBy?: string;
  evaluationDate?: string;
  createdAt?: string;
}

const qualityMap: Record<number, string> = { 1: 'Dolent', 2: 'Regular', 3: 'Bo', 4: 'Excel·lent' };
const criticalities = ['Baixa', 'Mitjana', 'Alta'];
const criteria = ['Històric', 'Mostra', 'Imposició o requisit', 'Certificat', 'Gerència', 'Acreditació', 'Visita', 'Recomanació'];

// Format number to decimal places, handle null/NaN/non-numeric values
const fmt = (v: unknown, dec = 2): string =>
  v == null || isNaN(Number(v)) ? '—' : Number(v).toFixed(dec);

export function SupplierEvaluationTab({ supplierId }: { supplierId: string }) {
  const { user } = useAuth();
  const userFullName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const [evaluations, setEvaluations] = useState<SupplierEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    productService: '',
    criticality: '',
    criterion: '',
    quality: 0,
    serviceLevel: 0,
    price: 0,
    environmental: 0,
    qualification: '',
    evaluatedBy: userFullName,
    evaluationDate: new Date().toISOString().split('T')[0],
  });

  const loadEvaluations = async () => {
    setLoading(true);
    try {
      const response = await api.get<{ success: boolean; data: SupplierEvaluation[] }>(
        `/supplier-evaluations/${supplierId}`
      );
      setEvaluations(response.data || []);
    } catch (err) {
      console.error('[v0] Error loading evaluations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadEvaluations();
  }, [supplierId]);

  // Update evaluatedBy when user changes or form opens
  useEffect(() => {
    if (showForm && userFullName) {
      setFormData(prev => ({
        ...prev,
        evaluatedBy: userFullName,
      }));
    }
  }, [showForm, userFullName]);

  const total = formData.quality && formData.serviceLevel && formData.price && formData.environmental
    ? parseFloat((((formData.quality + formData.serviceLevel + formData.price + formData.environmental) / 4).toFixed(2)))
    : 0;

  const auto_qualification = total >= 2 ? 'APTE' : 'NO APTE';

  const handleSubmit = async () => {
    try {
      const payload = {
        supplierId,
        ...formData,
        qualification: formData.qualification || auto_qualification,
        quality: parseInt(String(formData.quality), 10),
        serviceLevel: parseInt(String(formData.serviceLevel), 10),
        price: parseInt(String(formData.price), 10),
        environmental: parseInt(String(formData.environmental), 10),
      };

      await api.post('/supplier-evaluations', payload);
      setShowForm(false);
      setFormData({
        productService: '',
        criticality: '',
        criterion: '',
        quality: 0,
        serviceLevel: 0,
        price: 0,
        environmental: 0,
        qualification: '',
        evaluatedBy: userFullName,
        evaluationDate: new Date().toISOString().split('T')[0],
      });
      await loadEvaluations();
    } catch (err) {
      console.error('[v0] Error creating evaluation:', err);
    }
  };

  const handleDelete = async (evaluationId: string) => {
    if (!confirm('Estàs segur que vols eliminar aquesta avaluació?')) return;
    try {
      await api.delete(`/supplier-evaluations/${evaluationId}`);
      await loadEvaluations();
    } catch (err) {
      console.error('[v0] Error deleting evaluation:', err);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Carregant...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Nova Avaluació Button */}
      <div className="flex justify-end">
        <Button onClick={() => setShowForm(!showForm)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Nova avaluació
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="border-border bg-white">
          <CardHeader>
            <CardTitle>Nova Avaluació</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Producte o servei</label>
              <input
                type="text"
                value={formData.productService}
                onChange={(e) => setFormData(prev => ({ ...prev, productService: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Descripció del producte/servei"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Criticitat</label>
                <select
                  value={formData.criticality}
                  onChange={(e) => setFormData(prev => ({ ...prev, criticality: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona</option>
                  {criticalities.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Criteri</label>
                <select
                  value={formData.criterion}
                  onChange={(e) => setFormData(prev => ({ ...prev, criterion: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Selecciona</option>
                  {criteria.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* 4 Rating Fields */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'quality', label: 'Qualitat' },
                { key: 'serviceLevel', label: 'Termini/Nivell servei' },
                { key: 'price', label: 'Preu' },
                { key: 'environmental', label: 'Gestió ambiental' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium mb-1">{label}</label>
                  <select
                    value={formData[key as keyof typeof formData] || 0}
                    onChange={(e) => setFormData(prev => ({ ...prev, [key]: parseInt(e.target.value, 10) }))}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value={0}>Selecciona</option>
                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} - {qualityMap[n]}</option>)}
                  </select>
                </div>
              ))}
            </div>

            {/* Total and Qualification */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">TOTAL (media)</label>
                <div className="px-3 py-2 bg-muted rounded-lg text-sm font-semibold">{fmt(total, 2)} / 4</div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Qualificació</label>
                <div className="flex gap-2">
                  <select
                    value={formData.qualification}
                    onChange={(e) => setFormData(prev => ({ ...prev, qualification: e.target.value }))}
                    className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Auto ({auto_qualification})</option>
                    <option value="APTE">APTE</option>
                    <option value="NO APTE">NO APTE</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Evaluated by and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Realitzada per</label>
                <input
                  type="text"
                  value={formData.evaluatedBy}
                  onChange={(e) => setFormData(prev => ({ ...prev, evaluatedBy: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Data avaluació</label>
                <input
                  type="date"
                  value={formData.evaluationDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, evaluationDate: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
              <Button onClick={handleSubmit}>Desa</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {evaluations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground border rounded-lg">
          <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>Sense avaluacions registrades</p>
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted">
                    <TableHead>Producte/servei</TableHead>
                    <TableHead>Criticitat</TableHead>
                    <TableHead>Criteri</TableHead>
                    <TableHead className="text-center">Qualitat</TableHead>
                    <TableHead className="text-center">Termini/Servei</TableHead>
                    <TableHead className="text-center">Preu</TableHead>
                    <TableHead className="text-center">Gestió ambiental</TableHead>
                    <TableHead className="text-center font-bold">TOTAL</TableHead>
                    <TableHead>Qualificació</TableHead>
                    <TableHead>Realitzada per</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-center">Accions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {evaluations.map((evaluation) => (
                    <TableRow key={evaluation.id} className="hover:bg-muted/50">
                      <TableCell className="text-sm">{evaluation.productService || '-'}</TableCell>
                      <TableCell className="text-sm">{evaluation.criticality || '-'}</TableCell>
                      <TableCell className="text-sm">{evaluation.criterion || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{evaluation.quality || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{evaluation.serviceLevel || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{evaluation.price || '-'}</TableCell>
                      <TableCell className="text-center text-sm">{evaluation.environmental || '-'}</TableCell>
                      <TableCell className="text-center font-bold text-sm">{fmt(evaluation.total, 2)} / 4</TableCell>
                      <TableCell>
                        <Badge className={evaluation.qualification === 'APTE' ? 'bg-green-500/10 text-green-700' : 'bg-red-500/10 text-red-700'}>
                          {evaluation.qualification || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{evaluation.evaluatedBy || '-'}</TableCell>
                      <TableCell className="text-sm">{evaluation.evaluationDate || '-'}</TableCell>
                      <TableCell className="text-center">
                        <button
                          onClick={() => handleDelete(evaluation.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
